import { z } from 'zod';

export const LeaderboardEntry = z.object({
    userId: z.string(),
    score: z.number(),
});

export type LeaderboardEntry = z.infer<typeof LeaderboardEntry>;

export const MetricWindows = z.object({
    week: z.array(LeaderboardEntry),
    twoWeeks: z.array(LeaderboardEntry),
    thirtyDays: z.array(LeaderboardEntry),
    ninetyDays: z.array(LeaderboardEntry),
});

export type MetricWindows = z.infer<typeof MetricWindows>;

export type LeadsWindowKey = keyof MetricWindows;

export const LEADS_WINDOW_DAYS: Record<LeadsWindowKey, number> = {
    week: 7,
    twoWeeks: 14,
    thirtyDays: 30,
    ninetyDays: 90,
};

export const UserActivityScore = LeaderboardEntry;
export type UserActivityScore = LeaderboardEntry;
export const UserActivityWindows = MetricWindows;
export type UserActivityWindows = MetricWindows;

export const MetricDailyScores = z.record(z.string(), z.record(z.string(), z.number()));
export type MetricDailyScores = z.infer<typeof MetricDailyScores>;

export const UserActivityDailyScores = MetricDailyScores;
export type UserActivityDailyScores = MetricDailyScores;

export const MetricBlock = z.object({
    lastProcessedDay: z.string(),
    calculatedAt: z.string(),
    dailyScores: MetricDailyScores,
    windows: MetricWindows,
});

export type MetricBlock = z.infer<typeof MetricBlock>;

export const ProjectMetricDailyScores = z.record(z.string(), MetricDailyScores);
export type ProjectMetricDailyScores = z.infer<typeof ProjectMetricDailyScores>;

export const ProjectMetricBlock = z.object({
    lastProcessedDay: z.string(),
    calculatedAt: z.string(),
    dailyScores: ProjectMetricDailyScores,
});

export type ProjectMetricBlock = z.infer<typeof ProjectMetricBlock>;

export const LeadsTeamRef = z.object({
    id: z.string(),
    name: z.string().nullable(),
});

export type LeadsTeamRef = z.infer<typeof LeadsTeamRef>;

export const LeadsUserStats = z.object({
    projects: z.number(),
    components: z.number(),
    teams: z.array(LeadsTeamRef),
});

export type LeadsUserStats = z.infer<typeof LeadsUserStats>;

export const LeadsProfileStats = z.object({
    calculatedAt: z.string(),
    byUser: z.record(z.string(), LeadsUserStats),
});

export type LeadsProfileStats = z.infer<typeof LeadsProfileStats>;

export const LeadsLeaderboardMetrics = z.object({
    activity: MetricBlock,
    credits: MetricBlock,
    messages: MetricBlock,
    editorMinutes: MetricBlock,
    editorMinutesByProject: ProjectMetricBlock,
    projectOpensByProject: ProjectMetricBlock,
    projectCreationsByProject: ProjectMetricBlock,
    profileStats: LeadsProfileStats,
});

export type LeadsLeaderboardMetrics = z.infer<typeof LeadsLeaderboardMetrics>;

export const LEADS_METRIC_NAMES = [
    'activity',
    'credits',
    'messages',
    'editorMinutes',
    'editorMinutesByProject',
    'projectOpensByProject',
    'projectCreationsByProject',
] as const;

export type LeadsMetricName = (typeof LEADS_METRIC_NAMES)[number];

export const DEFAULT_CATCH_UP_DAYS = LEADS_WINDOW_DAYS.week;

export const LeadsMetricsCatchUpQuery = z.object({
    days: z.coerce.number().int().min(1).max(LEADS_WINDOW_DAYS.ninetyDays).default(DEFAULT_CATCH_UP_DAYS),
    metrics: z
        .string()
        .default(LEADS_METRIC_NAMES.join(','))
        .transform((value) =>
            value
                .split(',')
                .map((name) => name.trim())
                .filter(Boolean),
        )
        .pipe(z.array(z.enum(LEADS_METRIC_NAMES)).min(1)),
});

export type LeadsMetricsCatchUpQuery = z.infer<typeof LeadsMetricsCatchUpQuery>;
